/**
 * Upload/Verwaltung der eigenen Rechtsdokumente eines Händlers
 * (Datenschutzerklärung, AGB, Kaufvertrag) als PDF.
 *
 * - Upload via POST /api/upload/image (akzeptiert PDF) → liefert URL
 * - Speicherung der URL via PATCH /api/users/me/preferences
 * - Das Kaufvertrag-PDF ersetzt serverseitig den system-generierten Vertrag.
 */
import { useEffect, useState } from "react";
import { FileText, Upload, Check, Trash2, ExternalLink, Loader2, History, Info, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";

const DOC_TYPES = [
    { key: "doc_privacy_url", label: "Datenschutzerklärung (Händler)", use: "Wird auf Ihren öffentlichen Kundenseiten verlinkt und Verträgen beigelegt." },
    { key: "doc_privacy_manufacturer_url", label: "Datenschutzerklärung (Hersteller / Importeur)", hint: "Separate Datenschutzerklärung des Herstellers/Importeurs — wird zusätzlich zur Händler-Erklärung verlinkt.", use: "Zusätzlich zur Händler-Datenschutzerklärung auf öffentlichen Seiten verlinkt." },
    { key: "doc_terms_url", label: "AGB (allgemein)", use: "Vertragsgrundlage; wird Kaufverträgen beigelegt und auf öffentlichen Seiten verlinkt." },
    { key: "doc_terms_new_url", label: "AGB für Neufahrzeuge / Tageszulassungen", hint: "Gilt für fabrikneue Fahrzeuge und Tageszulassungen.", use: "Wird bei Verträgen für Neufahrzeuge/Tageszulassungen verwendet." },
    { key: "doc_terms_used_url", label: "AGB für Gebrauchte / Vorführer", hint: "Gilt für Gebrauchtfahrzeuge und Vorführfahrzeuge.", use: "Wird bei Verträgen für Gebraucht-/Vorführfahrzeuge verwendet." },
    { key: "doc_contract_url", label: "Kaufvertrag", hint: "Ersetzt den automatisch erzeugten Kaufvertrag beim Sofortkauf.", use: "Ersetzt beim Sofortkauf den automatisch erzeugten Kaufvertrag." },
];

const fileUrl = (rel) => (rel?.startsWith("http") ? rel : `${process.env.REACT_APP_BACKEND_URL}${rel}`);
const todayISO = () => new Date().toISOString().slice(0, 10);

export default function LegalDocsUploader({ currentUser }) {
    const [docs, setDocs] = useState({
        doc_privacy_url: currentUser.doc_privacy_url || "",
        doc_privacy_manufacturer_url: currentUser.doc_privacy_manufacturer_url || "",
        doc_terms_url: currentUser.doc_terms_url || "",
        doc_terms_new_url: currentUser.doc_terms_new_url || "",
        doc_terms_used_url: currentUser.doc_terms_used_url || "",
        doc_contract_url: currentUser.doc_contract_url || "",
    });
    const [busy, setBusy] = useState("");
    const [linkDraft, setLinkDraft] = useState({});
    const [validFrom, setValidFrom] = useState({});
    const [versions, setVersions] = useState({});

    useEffect(() => {
        api.get("/users/me/legal-docs/versions").then((r) => setVersions(r.data?.versions || {})).catch(() => {});
    }, []);

    const persist = async (key, url) => {
        await api.patch("/users/me/preferences", { [key]: url });
        setDocs((d) => ({ ...d, [key]: url }));
        if (url) {
            try {
                const mode = url.startsWith("http") ? "link" : "pdf";
                const r = await api.post(`/users/me/legal-docs/${key}/version`, { value: url, mode, valid_from: validFrom[key] || todayISO() });
                setVersions((v) => ({ ...v, [key]: [r.data.entry, ...(v[key] || [])] }));
            } catch { /* Versionsverlauf ist optional */ }
        }
    };

    const saveLink = async (key) => {
        const url = (linkDraft[key] || "").trim();
        if (!/^https?:\/\/.+/i.test(url)) {
            toast.error("Bitte eine gültige URL (mit https://) eingeben");
            return;
        }
        setBusy(key);
        try {
            await persist(key, url);
            setLinkDraft((d) => ({ ...d, [key]: "" }));
            toast.success("Link gespeichert");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
        } finally {
            setBusy("");
        }
    };

    const onUpload = async (key, file) => {
        if (!file) return;
        if (!file.name.toLowerCase().endsWith(".pdf")) {
            toast.error("Bitte eine PDF-Datei wählen");
            return;
        }
        setBusy(key);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
            await persist(key, data.url);
            toast.success("Dokument hochgeladen");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upload fehlgeschlagen");
        } finally {
            setBusy("");
        }
    };

    const onRemove = async (key) => {
        setBusy(key);
        try {
            await persist(key, "");
            toast.success("Dokument entfernt");
        } catch {
            toast.error("Entfernen fehlgeschlagen");
        } finally {
            setBusy("");
        }
    };

    return (
        <div className="mt-4 space-y-2" data-testid="legal-docs-uploader">
            <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Eigene Rechtsdokumente (PDF)</div>
            {DOC_TYPES.map(({ key, label, hint, use }) => {
                const url = docs[key];
                const loading = busy === key;
                return (
                    <div key={key} className="flex items-center gap-3 rounded-sm border border-gray-200 p-3" data-testid={`legal-doc-row-${key}`}>
                        <FileText className="w-4 h-4 text-gray-400 shrink-0" />
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-bold text-[#111827] flex items-center gap-1.5">
                                {label}
                                {use && (
                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <button type="button" className="text-gray-400 hover:text-[#111827]" title="Wo wird dieses Dokument genutzt?" data-testid={`legal-doc-info-${key}`}>
                                                <Info className="w-3.5 h-3.5" />
                                            </button>
                                        </PopoverTrigger>
                                        <PopoverContent align="start" className="w-72 text-sm leading-relaxed text-gray-700">{use}</PopoverContent>
                                    </Popover>
                                )}
                            </div>
                            {hint && <div className="text-[11px] text-gray-500">{hint}</div>}
                            {url && (
                                <a href={fileUrl(url)} target="_blank" rel="noopener noreferrer" className="text-[11px] text-blue-700 inline-flex items-center gap-1 hover:underline" data-testid={`legal-doc-view-${key}`}>
                                    <Check className="w-3 h-3 text-green-600" /> {url.startsWith("http") ? "Verlinkt — ansehen" : "Hochgeladen — ansehen"} <ExternalLink className="w-3 h-3" />
                                </a>
                            )}
                            {/* Jun-2026 (User-Wunsch 3b) — alternativ externen Link statt Upload hinterlegen */}
                            <div className="mt-1.5 flex items-center gap-1.5">
                                <input
                                    type="url"
                                    value={linkDraft[key] ?? ""}
                                    onChange={(e) => setLinkDraft((d) => ({ ...d, [key]: e.target.value }))}
                                    onKeyDown={(e) => { if (e.key === "Enter") saveLink(key); }}
                                    placeholder="oder Link (https://…) einfügen"
                                    className="flex-1 min-w-0 text-[11px] border border-gray-300 rounded-sm px-2 py-1 font-mono-num"
                                    data-testid={`legal-doc-link-input-${key}`}
                                />
                                <button type="button" onClick={() => saveLink(key)} disabled={loading}
                                    className="text-[11px] font-bold inline-flex items-center gap-1 border border-gray-300 rounded-sm px-2 py-1 hover:bg-gray-100 shrink-0"
                                    data-testid={`legal-doc-link-save-${key}`}>
                                    <ExternalLink className="w-3 h-3" /> Link
                                </button>
                            </div>
                            {/* Jun-2026 (User-Wunsch) — „Gültig ab" + Versionsverlauf (GoBD) */}
                            <div className="mt-1.5 flex flex-wrap items-center gap-2">
                                <label className="text-[11px] text-gray-500 inline-flex items-center gap-1">
                                    <CalendarClock className="w-3 h-3" /> Gültig ab
                                    <input type="date" value={validFrom[key] || todayISO()}
                                        onChange={(e) => setValidFrom((v) => ({ ...v, [key]: e.target.value }))}
                                        className="border border-gray-300 rounded-sm px-1.5 py-0.5 font-mono-num"
                                        data-testid={`legal-doc-validfrom-${key}`} />
                                </label>
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <button type="button" className="text-[11px] text-gray-600 inline-flex items-center gap-1 hover:text-[#111827]" data-testid={`legal-doc-history-${key}`}>
                                            <History className="w-3 h-3" /> Verlauf ({(versions[key] || []).length})
                                        </button>
                                    </PopoverTrigger>
                                    <PopoverContent align="start" className="w-80 text-sm" data-testid={`legal-doc-history-content-${key}`}>
                                        <div className="font-bold mb-2 text-[#111827]">Versionsverlauf</div>
                                        {(versions[key] || []).length === 0
                                            ? <div className="text-gray-500 text-xs">Noch keine Versionen erfasst.</div>
                                            : <ul className="space-y-1.5 max-h-60 overflow-auto">
                                                {(versions[key] || []).map((v) => (
                                                    <li key={v.id} className="text-xs flex items-center justify-between gap-2 border-b border-gray-100 pb-1">
                                                        <span><strong>v{v.version}</strong> · gültig ab {v.valid_from} · {v.mode === "pdf" ? "PDF" : "Link"}</span>
                                                        <a href={fileUrl(v.value)} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline shrink-0">ansehen</a>
                                                    </li>
                                                ))}
                                              </ul>}
                                    </PopoverContent>
                                </Popover>
                            </div>
                        </div>
                        <label className={`text-xs font-bold rounded-sm border-2 border-[#111827] px-3 py-1.5 cursor-pointer hover:bg-[#FFCC00]/10 ${loading ? "opacity-50 pointer-events-none" : ""}`} data-testid={`legal-doc-upload-${key}`}>
                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (<span className="inline-flex items-center gap-1"><Upload className="w-3.5 h-3.5" /> {url ? "Ersetzen" : "Hochladen"}</span>)}
                            <input type="file" accept="application/pdf,.pdf" className="hidden" disabled={loading} onChange={(e) => onUpload(key, e.target.files?.[0])} data-testid={`legal-doc-input-${key}`} />
                        </label>
                        {url && (
                            <button type="button" onClick={() => onRemove(key)} disabled={loading} className="text-gray-400 hover:text-red-600 shrink-0" data-testid={`legal-doc-remove-${key}`} aria-label={`${label} entfernen`}>
                                <Trash2 className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
